import type { RouteRecordNameGeneric, Router, RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import type { IAppRoute } from './router-interface';
import { isNullOrEmpty } from '@powerduck/common/utils/is-null-or-empty';
import { PortalUtils } from '@powerduck/common/utils/utils';
import NProgress from 'nprogress';
import { createRouter, createWebHistory } from 'vue-router';
import { AppConfig } from '../app-config';
import AppRoutes from './../app-routes';
import { AppState } from '../app-state';
import 'nprogress/nprogress.css';

const AppRouterUtils = (() => {
	let initialized = false;
	let currentRouter: Router = null;
	let previousRoute: RouteLocationNormalizedLoadedGeneric = null;
	let nextTo: RouteLocationNormalizedLoadedGeneric = null;

	const getRouteId = (route: AppRoute): string => {
		if (route.name != null && route.name.length > 0) {
			return route.name;
		} else {
			return route.path.split('/')[1];
		}
	};

	const getRouteByName = (name: RouteRecordNameGeneric): AppRoute => {
		for (const key in AppRoutes) {
			const item: AppRoute = AppRoutes[key];
			if (item.name == name) {
				return item;
			}
		}

		return null;
	};

	const getPossibleInitialRoute = (): AppRoute => {
		try {
			const locPath = `/${location.pathname.split('/')[1]}/`;
			for (const key in AppRoutes) {
				const item: AppRoute = AppRoutes[key];
				if (item.path.startsWith(locPath)) {
					return item;
				} else if (`${item.path}/` == locPath) {
					return item;
				} else if (item.path == locPath) {
					return item;
				}
			}

			return null;
		} catch (e) {
			return null;
		}
	};

	const getPreviousRoute = (): IAppRoute => {
		if (previousRoute == null) {
			return null;
		}

		return getRouteByName(previousRoute.name as string);
	};

	const getRoutes = (): AppRoute[] => {
		const retVal = [];
		for (const key in AppRoutes) {
			const route: AppRoute = AppRoutes[key];
			if (route.path.indexOf('/') != 0) {
				console.error(`Route invalid [${route.path}]`);
			}

			retVal.push(AppRoutes[key]);
		}

		return retVal;
	};

	const handlePageSpecificClass = (to, from) => {
		const toRoute: AppRoute = getRouteByName(to.name);
		const clsList = document.body.classList;

		clsList.remove('inv-no-menu');
		clsList.remove('inv-no-header');
		clsList.remove('inv-full-screen');
		clsList.remove('inv-header-visible');
		clsList.remove('inv-layout-buyer');
		clsList.remove('inv-layout-marketplace');

		if (toRoute != null) {
			if (toRoute.hideHeader == true) {
				clsList.add('inv-no-header');
			}

			if (toRoute.hideMenu == true && !AppState.user) {
				clsList.add('inv-no-menu');
			}

			if (toRoute.fullScreen == true) {
				clsList.add('inv-full-screen');
			}

			const layout = toRoute.layout || 'admin';
			if (layout === 'buyer' || layout === 'marketplace') {
				clsList.add('inv-header-visible');
				clsList.add(`inv-layout-${layout}`);
			}

			// Redesigned pages: toggle global class so structural CSS
			// (navbar hide, content padding) applies instantly from
			// app-overrides.css — no FOUC between lazy chunks.
			if (toRoute.customLayout) {
				clsList.add('iu-active');
				clsList.add('iu-loading');
			} else {
				clsList.remove('iu-active');
				clsList.remove('iu-loading');
			}
		} else {
			clsList.remove('iu-active');
			clsList.remove('iu-loading');
		}

		document.body.classList.remove(`apg-${getRouteId(from)}`);
		// Clean up edit-event alias when leaving create routes
		if (getRouteId(from) === 'create-event' || getRouteId(from) === 'create-event-bo') {
			document.body.classList.remove('apg-edit-event');
		}
		document.body.classList.add(`apg-${getRouteId(to)}`);
		// Create routes share the same component & styles as edit-event
		if (getRouteId(to) === 'create-event' || getRouteId(to) === 'create-event-bo') {
			document.body.classList.add('apg-edit-event');
		}
	};

	const createNewRouter = (): Router => {
		const router = createRouter({
			history: createWebHistory(),
			routes: getRoutes(),
		});

		router.onError((err) => {
			if (err.name == 'ChunkLoadError') {
				const lastTry = Number(localStorage.getItem('errLastReload') || '0');
				if (new Date().getTime() - lastTry > 3000) {
					localStorage.setItem('errLastReload', new Date().getTime().toString());

					if (nextTo != null && !isNullOrEmpty(nextTo.path)) {
						location.href = nextTo.path;
					} else {
						location.reload();
					}
				}
			}
		});

		router.beforeEach((
			to,
			from,
			next,
		) => {
			nextTo = to;
			previousRoute = from;
			handlePageSpecificClass(to, from);

			//Remove any orphaned dropdown clones that were appended to body
			document.querySelectorAll('.dt-dropdown-button-cloned').forEach(el => el.remove());

			next();
		});

		// Not the nicests way that hides menu after router page navigation on mobiles
		router.afterEach((to, from) => {
			NProgress.done();
			PortalUtils.handleMobileMenuClick();
			// Reveal content after lazy chunk has loaded and component is mounted.
			// requestAnimationFrame ensures the new DOM has painted before we fade in.
			requestAnimationFrame(() => {
				document.body.classList.remove('iu-loading');
			});
		});

		router.beforeResolve((
			to,
			from,
			next,
		) => {
			// If this isn't an initial page load.
			if (to.name) {
				if (PortalUtils.treatAsMobileDevice()) {
					document.querySelectorAll<HTMLElement>('.mobileLoader').forEach((el) => {
						el.classList.add('mobileLoaderActive');
						el.innerHTML = AppConfig.blockerHtml;
					});
				}

				NProgress.start();
			}

			next();
		});

		router.afterEach((to, from) => {
			if (PortalUtils.treatAsMobileDevice()) {
				const clearLoader = () => {
					document.querySelectorAll<HTMLElement>('.mobileLoader').forEach((el) => {
						el.classList.remove('mobileLoaderActive');
						el.innerHTML = '';
					});
				};

				clearLoader();
				setTimeout(clearLoader, 100);
			}
		});

		initialized = true;

		return router;
	};

	const getRouter = (): Router => {
		if (currentRouter == null) {
			currentRouter = createNewRouter();
		}

		return currentRouter;
	};

	return {
		getRouteByName,
		getPossibleInitialRoute,
		getPreviousRoute,
		getRouter,
	};
})();

export interface AppRoute {
	path: string;
	component: any;
	name: string;
	title?: () => string;
	hideMenu?: boolean;
	hideHeader?: boolean;
	fullScreen?: boolean;
	customLayout?: boolean;
	layout?: import('./router-interface').SpaLayout;
}

export class AppRouter {
	static get currentRouter(): Router {
		return AppRouterUtils.getRouter();
	}

	static get previousRoute(): IAppRoute {
		return AppRouterUtils.getPreviousRoute();
	}

	static getRouteByName(name: RouteRecordNameGeneric): AppRoute {
		return AppRouterUtils.getRouteByName(name);
	}

	static getPossibleInitialRoute(): AppRoute {
		return AppRouterUtils.getPossibleInitialRoute();
	}
}
